import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Star, Trash2 } from "lucide-react";
import { requireAdmin } from "@/lib/admin-guard";
import {
  adminListContentUpdates,
  adminUpsertContentUpdate,
  adminDeleteContentUpdate,
  type ContentUpdate,
} from "@/lib/content-updates.functions";

export const Route = createFileRoute("/admin_/content-updates")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "What's New — Admin | Recovery Trauma™" },
      { name: "description", content: "Manage the member What's New feed." },
      { property: "og:title", content: "What's New — Admin | Recovery Trauma™" },
      { property: "og:description", content: "Manage the member What's New feed." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminContentUpdates,
});

const EMPTY = {
  content_type: "Learning Hub",
  title: "",
  description: "",
  route: "/learn",
  icon: "🌿",
  cta_label: "Start Exploring",
  change_kind: "new" as "new" | "updated",
  is_featured: false,
  is_published: true,
};

function AdminContentUpdates() {
  const list = useServerFn(adminListContentUpdates);
  const upsert = useServerFn(adminUpsertContentUpdate);
  const remove = useServerFn(adminDeleteContentUpdate);
  const qc = useQueryClient();
  const [form, setForm] = useState({ ...EMPTY });

  const { data, isLoading } = useQuery({
    queryKey: ["content-updates", "admin"],
    queryFn: () => list(),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["content-updates"] });
  };

  const save = useMutation({
    mutationFn: (payload: Record<string, unknown>) => upsert({ data: payload as never }),
    onSuccess: () => {
      setForm({ ...EMPTY });
      invalidate();
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => remove({ data: { id } as never }),
    onSuccess: invalidate,
  });

  const toggleFeatured = (item: ContentUpdate) =>
    save.mutate({
      id: item.id,
      content_type: item.content_type,
      title: item.title,
      description: item.description,
      route: item.route,
      icon: item.icon,
      cta_label: item.cta_label,
      change_kind: item.change_kind === "updated" ? "updated" : "new",
      is_featured: !item.is_featured,
      is_published: item.is_published,
      published_at: item.published_at,
    });

  const input =
    "w-full rounded-xl border border-gold/30 bg-card px-3 py-2 text-sm text-foreground";

  return (
    <div className="mx-auto max-w-3xl px-4 py-10">
      <Link to="/admin" className="text-sm text-[var(--gold-deep)] hover:underline">
        ← Admin
      </Link>
      <h1 className="mt-3 font-display text-2xl text-foreground sm:text-3xl">
        What's new in your membership
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Everything published here appears automatically at the top of /membership for
        active members. Items carry a NEW badge for 30 days.
      </p>

      <form
        className="mt-6 space-y-3 rounded-2xl border border-gold/25 bg-card p-5"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate({ ...form });
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            Category
            <input
              className={input}
              value={form.content_type}
              onChange={(e) => setForm({ ...form, content_type: e.target.value })}
            />
          </label>
          <label className="text-sm">
            Icon
            <input
              className={input}
              value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
            />
          </label>
        </div>
        <label className="block text-sm">
          Title
          <input
            required
            className={input}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </label>
        <label className="block text-sm">
          Description
          <textarea
            rows={3}
            className={input}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">
            Route
            <input
              required
              className={input}
              value={form.route}
              onChange={(e) => setForm({ ...form, route: e.target.value })}
            />
          </label>
          <label className="text-sm">
            CTA label
            <input
              className={input}
              value={form.cta_label}
              onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
            />
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.change_kind === "updated"}
              onChange={(e) =>
                setForm({ ...form, change_kind: e.target.checked ? "updated" : "new" })
              }
            />
            Mark as UPDATED (not new)
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_featured}
              onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
            />
            Feature at the top
          </label>
          <label className="inline-flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.is_published}
              onChange={(e) => setForm({ ...form, is_published: e.target.checked })}
            />
            Published
          </label>
        </div>
        <button type="submit" className="btn-gold" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          Add update
        </button>
        {save.isError ? (
          <p className="text-sm text-destructive">{(save.error as Error).message}</p>
        ) : null}
      </form>

      <div className="mt-8 space-y-3">
        {isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-[var(--gold-deep)]" />
        ) : (
          (data ?? []).map((item) => (
            <div
              key={item.id}
              className="flex items-start gap-3 rounded-2xl border border-gold/20 bg-card p-4"
            >
              <span aria-hidden className="text-xl">
                {item.icon}
              </span>
              <div className="flex-1">
                <p className="font-medium text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground">
                  {item.content_type} · {new Date(item.published_at).toLocaleDateString("en-GB")}
                  {item.is_published ? "" : " · draft"}
                  {item.change_kind === "updated" ? " · updated" : ""}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">{item.route}</p>
              </div>
              <button
                type="button"
                aria-label={item.is_featured ? "Unfeature" : "Feature"}
                onClick={() => toggleFeatured(item)}
                className={`rounded-full p-2 ${item.is_featured ? "text-[var(--gold-deep)]" : "text-muted-foreground"}`}
              >
                <Star className="h-4 w-4" fill={item.is_featured ? "currentColor" : "none"} />
              </button>
              <button
                type="button"
                aria-label="Delete"
                onClick={() => del.mutate(item.id)}
                className="rounded-full p-2 text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
